import React from "react";
import { Link } from "react-router-dom";

/* ============================================================
   /about — Paul's story + build partner credits.

   This page exists for two reasons:
   1. Give Paul a dedicated page to point people at when they ask
      "what's the story?" — his founder narrative is the marketing.
   2. Give proper on-site attribution to Emergent as the platform
      that made this build possible, and to the AI models (Claude,
      Nano Banana, Suno, OpenAI TTS) that power the product.
   ============================================================ */

export default function About() {
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16">
      <div className="font-mono text-xs tracking-[0.5em] text-yt-yellow mb-4">OUR STORY</div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.9] mb-8">
        FOR THE KID WHO WRITES BARS IN A NOTEBOOK
        <br />
        <span className="text-yt-red">BUT FREEZES IN FRONT OF A CROWD.</span>
      </h1>

      <div className="space-y-8 font-body text-lg leading-relaxed text-white/85">
        <section>
          <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3">THE FOUNDER</div>
          <p>
            Young Tongues was built by <strong className="text-white">Paul DeGagné</strong> — a farmer in Alberta, Canada.
            Not a Silicon Valley engineer. Not a music producer. A farmer with a traumatic brain injury
            (irreparable left frontal-temporal lobe damage from a car accident), short-term memory loss,
            and a heart that has to be careful about adrenaline dumps.
          </p>
          <p className="mt-4">
            Before this platform, Paul was <strong className="text-white">computer-illiterate</strong>.
            He learned to code after being scammed out of roughly $400,000 of his kids' college fund
            by an online trading scheme. He decided that the way through was <em>to build</em> — not to
            hire, not to wait, not to complain. To build the tools himself.
          </p>
        </section>

        <section>
          <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3">THE PLATFORM</div>
          <p>
            Young Tongues is <strong className="text-white">two things under one roof</strong>: a live
            AI rap battle arena where writers face off 1-on-1, and a monthly community-crowned song
            chart where songwriters drop full tracks and let the community vote. Two crowns. One profile.
            Two ways to win.
          </p>
          <p className="mt-4">
            The rule is <strong className="text-white">writer-first</strong>: no auto-generated lyrics,
            no bot swarms, no loop scams. The AI handles the beat, the voice, and the judge. The human
            handles the words. That's the whole promise.
          </p>
        </section>

        <section>
          <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3">THE BUILD PARTNER</div>
          <p>
            Young Tongues was built in partnership with{" "}
            <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer"
               data-testid="about-emergent-link"
               className="text-yt-yellow underline underline-offset-4 hover:text-white">
              Emergent
            </a>
            {" "}— an AI-native coding platform that let a farmer with no traditional engineering background
            ship a real full-stack music tech application. Every line of this codebase was written in
            collaboration with Emergent's AI builders.
          </p>
          <p className="mt-4">
            Paul is grateful to the Emergent team for the platform — for giving a solo builder
            with a story and an idea a real shot at shipping something that could stand on its own.
          </p>
        </section>

        <section>
          <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3">THE AI PARTNERS</div>
          <p>
            The platform is powered by a stack of best-in-class models:
          </p>
          <ul className="mt-4 space-y-2 font-mono text-sm">
            <li>
              <span className="text-yt-yellow">◆</span> <strong className="text-white">Claude Sonnet 4.5</strong> (Anthropic) —
              rap verse generation, lyric auto-arrangement, and Judge DeGagné scoring
            </li>
            <li>
              <span className="text-yt-yellow">◆</span> <strong className="text-white">Suno V5</strong> —
              full-song vocal + instrumental generation for The Charts
            </li>
            <li>
              <span className="text-yt-yellow">◆</span> <strong className="text-white">Nano Banana (Gemini)</strong> —
              character portrait and arena artwork generation
            </li>
            <li>
              <span className="text-yt-yellow">◆</span> <strong className="text-white">OpenAI TTS + Whisper</strong> —
              Judge DeGagné's voice and voice-typed verses
            </li>
            <li>
              <span className="text-yt-yellow">◆</span> <strong className="text-white">Runway Gen-4 Turbo</strong> —
              character motion loops on the battle stage
            </li>
          </ul>
        </section>

        <section>
          <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3">THE MISSION</div>
          <p>
            Every kid who ever wrote lyrics in a notebook and got told "you'll never make it" —
            this is for them. Every writer who freezes in a crowd but crushes it on the page —
            this is for them. Every farmer, factory worker, night-shift nurse, or single parent who
            has a verse in them and no way to get it out — <strong className="text-white">this is for them</strong>.
          </p>
          <p className="mt-4 text-white">
            Come pick a fight. Come drop a track. The crown is up for grabs.
          </p>
        </section>
      </div>

      {/* CTAs */}
      <div className="mt-12 flex flex-wrap gap-3">
        <Link
          to="/battles"
          data-testid="about-cta-arena"
          className="bg-yt-yellow text-black font-display text-lg tracking-widest px-6 py-3 shadow-brutal hover:bg-white"
        >
          ▶ ENTER THE ARENA
        </Link>
        <Link
          to="/charts"
          data-testid="about-cta-charts"
          className="border-2 border-yt-red text-yt-red font-display text-lg tracking-widest px-6 py-3 hover:bg-yt-red hover:text-white"
        >
          ▶ THE CHARTS
        </Link>
      </div>
    </div>
  );
}
